import classNames from 'classnames';
import { FC, memo, ReactNode, useCallback, useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import {
  clearPnpSessionStorage,
  isTrustlyCompleted,
  parseErrCode,
  parsePnpAction,
  PIQ_SESSION_TOKEN_KEY,
  PNP_ACTIONS,
  PNP_CANCELED_TYPE,
  PNP_COMPLETE_TYPE,
  TRUSTLY_COMPLETED_KEY,
} from './trustlyUtils';
import { CasumoIcon, CasumoLoaderIcon } from '../../assets/icons';
import { buildAppError } from '../../flow/errors/errorBuilder';
import { mapBeReasonToFe } from '../../flow/errors/errorCodes';
import useMarketInfo from '../../hooks/useMarketInfo';
import useNavigateTo from '../../hooks/useNavigateTo';
import usePnpAutoLogin from '../../hooks/usePnpAutoLogin';
import { useRegistrationStore } from '../../store/store';
import { TEST_ID } from '../../utils/constants';
import { IS_DEVELOPMENT, isTestEnvironment, URL } from '../../utils/envConstants';
import { GtmEvents } from '../../utils/gtm/gtm';
import { resetProgressInLocalStorage } from '../../utils/utils';

type TrustlyIframeProps = {
  onComplete: () => void;
};

const TrustlyHeader: FC<{ children?: ReactNode }> = ({ children }) => {
  const marketInfo = useMarketInfo();

  return (
    <div className={classNames('flex items-center justify-between w-full px-2xs md:px-md h-3xl border-b-2',
      'border-grey-0 md:h-[72px] overflow-hidden shrink-0')}
    style={{ backgroundColor: 'white' }}
    data-testid={TEST_ID.PNP + 'trustly-header'}>
      {/* Empty spacer to match back button width */}
      <div className="w-2xl" />
      {/* Desktop: title */}
      <div className="hidden sm:flex items-center justify-center gap-2xs w-full">
        <span className="font-extrabold text-[20px]" data-testid={TEST_ID.PNP + 'trustly-header-title'}>
          Talleta ja pelaa
        </span>
      </div>
      {/* Mobile: Casumo logo */}
      <a className="sm:hidden" href={`${URL}/${marketInfo?.market}`}>
        <CasumoIcon color="#4C00C2" />
      </a>
      {/* Spacer to balance the layout (close button removed during PnP deposit) */}
      <div className="w-2xl" />
      {children}
    </div>
  );
};

/**
 * Trustly PnP component for the mga_gra_pnp flow.
 *
 * Embeds the Trustly checkout URL (provided by the host app via the
 * `registration` messageBus payload as `payAndPlay.url`) in an iframe
 * inside the modal.
 */
const TrustlyIframe = ({ onComplete }: TrustlyIframeProps) => {
  const checkoutUrl = useRegistrationStore(
    (state) => state.metadata?.trustlyCheckoutUrl as string | undefined,
  );
  const setFormData = useRegistrationStore((state) => state.setFormData);
  const setMetadata = useRegistrationStore((state) => state.setMetadata);
  const [iframeLoading, setIframeLoading] = useState(true);
  const { showBoundary } = useErrorBoundary();
  const marketInfo = useMarketInfo();
  const { navigateToMarket } = useNavigateTo();
  const autoLogin = usePnpAutoLogin();

  const closeRegistration = useCallback(() => {
    clearPnpSessionStorage();
    resetProgressInLocalStorage(marketInfo.market);
    setMetadata({ isOpen: false });
    sessionStorage.setItem('fabric:registration:open', 'false');
    navigateToMarket(marketInfo.market);
  }, [marketInfo.market, navigateToMarket, setMetadata]);

  useEffect(() => {
    if (isTrustlyCompleted()) {
      onComplete();
    }
  }, [onComplete]);

  // Listen for postMessage from /fi/pay-and-play-callback
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (IS_DEVELOPMENT || isTestEnvironment()) {
        // eslint-disable-next-line no-console
        console.log('Received message event:', {
          origin: event.origin,
          data:   event.data,
        });
      }
      const eventType = event.data?.type;

      if (eventType === PNP_CANCELED_TYPE) {
        GtmEvents.logPnpTrustlyDone(false, 'user_canceled');
        closeRegistration();

        return;
      }

      if (eventType === PNP_COMPLETE_TYPE) {
        const { success, sessionToken, errCode, action, ...rest } = event.data.payload ?? {};

        if (success && sessionToken) {
          GtmEvents.logPnpTrustlyDone(true);
          sessionStorage.setItem(PIQ_SESSION_TOKEN_KEY, sessionToken);

          if (parsePnpAction(action) === PNP_ACTIONS.LOGIN) {
            autoLogin();

            return;
          }

          sessionStorage.setItem(TRUSTLY_COMPLETED_KEY, 'true');
          onComplete();
        } else {
          const { code: baseErrCode, availableAmount } = parseErrCode(errCode);

          GtmEvents.logPnpTrustlyDone(false, baseErrCode || undefined);
          const feCode = mapBeReasonToFe(baseErrCode);
          showBoundary(buildAppError(feCode, { ...rest, available_amount: availableAmount }));
        }
      }

    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [onComplete, setFormData, showBoundary, closeRegistration, autoLogin]);

  if (!checkoutUrl) {
    return (
      <>
        <TrustlyHeader />
        <div
          className="flex flex-col items-center justify-center h-full gap-sm p-lg self-stretch"
          data-testid={TEST_ID.PNP + 'trustly-awaiting-url'}
        >
          <div className="animate-spin rounded-md">
            <CasumoLoaderIcon width={64} height={64} />
          </div>
          {(IS_DEVELOPMENT || isTestEnvironment()) && (
            <button
              onClick={onComplete}
              className="mt-sm text-grey-40 text-xs underline"
            >
              Skip (dev only)
            </button>
          )}
        </div>
      </>
    );
  }

  return (
    <div className="flex flex-col w-full self-stretch" style={{ flex: '1 1 0%', minHeight: 0 }}>
      <TrustlyHeader />

      <div className="flex justify-center p-sm md:px-lg md:pt-md"
        style={{ flex: '1 1 0%', minHeight: 0, position: 'relative' }}>
        {iframeLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-sm bg-white"
            style={{ zIndex: 1 }}
            data-testid={TEST_ID.PNP + 'trustly-iframe-loading'}>
            <div className="animate-spin rounded-md">
              <CasumoLoaderIcon width={64} height={64} />
            </div>
          </div>
        )}
        <iframe
          src={checkoutUrl}
          title="Trustly"
          data-testid={TEST_ID.PNP + 'trustly-iframe'}
          onLoad={() => setIframeLoading(false)}
          allow="payment"
          className="w-full h-full md:w-[600px]"
          style={{
            border:       'none',
            display:      'block',
            borderRadius: '16px',
            boxShadow:    '0px 2px 3px 0px #1300311A',
          }}
        />
      </div>
      {(IS_DEVELOPMENT || isTestEnvironment()) && (
        <button
          onClick={onComplete}
          className="shrink-0 py-2xs text-grey-40 text-xs underline"
        >
            Skip (dev only)
        </button>
      )}
    </div>
  );
};

const Memoized = memo(TrustlyIframe);
Memoized.displayName = 'TrustlyIframe';

export default Memoized;
